import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * A useState like API for sourcing state from a query parameter.
 *
 * This hook only changes the named query parameter, preserving the rest of the querystring.
 *
 * By defult it will use history.replace, but this may be overridden with the second argument.
 */
export function useQueryParam<T extends string>(
  name: string,
  push?: boolean,
): [T | undefined, (setQuery: T | undefined) => void] {
  const history = useHistory();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const value = (params.get(name) as T) ?? undefined;

  const setValue = useCallback(
    (value: T | undefined) => {
      if (value) {
        params.set(name, value);
      } else {
        params.delete(name);
      }
      if (push) {
        history.push({
          pathname: location.pathname,
          search: params.toString(),
          state: location.state,
        });
      } else {
        history.replace({
          pathname: location.pathname,
          search: params.toString(),
          state: location.state,
        });
      }
    },
    [history, location.pathname, params, name, push, location.state],
  );

  return [value, setValue];
}
